import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';
import type { CreateStudentPayload } from './school.api';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type OnlineAdmissionStatus = 'pending' | 'admitted';

export interface OnlineAdmissionRow {
  id: string;
  name: string;
  gender: string | null;
  dob: string | null;
  mobile: string | null;
  whatsapp: string | null;
  fatherName: string | null;
  classId: string | null;
  className: string | null;
  sectionId: string | null;
  sectionName: string | null;
  place: string | null;
  sourceOfInfo: string | null;
  status: OnlineAdmissionStatus;
  appliedAt: string;
  admittedStudentId: string | null;
  /** Imported from the 2024 archive table (no status column in that source — see backend doc comment) — excluded from the default queue view unless includeArchived is set. */
  isArchivedImport: boolean;
}

export interface OnlineAdmissionListParams {
  page?: number;
  limit?: number;
  status?: OnlineAdmissionStatus;
  classId?: string;
  sectionId?: string;
  search?: string;
  includeArchived?: boolean;
}

/** School-tier "Online Admission" queue — see backend OnlineAdmissionService's doc comment: admit() is a thin adapter reusing StudentsService.create() unchanged, so the payload shape is exactly CreateStudentPayload (same one the regular Students admission form already sends). */
export const OnlineAdmissionApi = {
  list: async (params?: OnlineAdmissionListParams): Promise<PaginatedResult<OnlineAdmissionRow>> =>
    unwrap(await api.get('/online-admissions', { params })),

  getOne: async (id: string): Promise<OnlineAdmissionRow> => unwrap(await api.get(`/online-admissions/${id}`)),

  admit: async (id: string, payload: CreateStudentPayload) => unwrap(await api.post(`/online-admissions/${id}/admit`, payload)),

  /** PDF endpoint requires auth (TenantJwtGuard) so it can't be a plain link — fetched with the real bearer token, same pattern as IdCardsApi.previewHtml(). Caller opens the returned HTML in a new tab and lets the browser's print dialog save it. */
  pdfHtml: async (id: string): Promise<string> => {
    const res = await api.get(`/online-admissions/${id}/pdf`, { responseType: 'text' });
    return res.data as unknown as string;
  },
};
